import React from 'react';

import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import {
  APPROVED_TWP,
  ASSOCIATE_DECISION_PENDING,
  DLS_REFERRAL,
  INDEFINITE_RESTRICTIONS,
  PLACEMENT_DECISION_PENDING,
  TRANSFER_PENDING,
  TWP_BUT_PENDING_UPDATED_RESTRICTIONS,
} from 'src/config/AccommodationStatusNames';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { useAppSelector } from 'src/store/store';

interface RowItem {
  label: string;
  value: string;
  dataCy: string;
  condition?: boolean;
  href?: string;
}

const TopDetails = () => {
  const { placementDetails } = useAppSelector((state) => state.placementDetailsPage);

  // format time to be displayed in UI per hours or days
  const formatTime = (time: number) => {
    const unit = time < 100 ? 'Hours' : 'Days';
    const formattedTime = time < 100 ? time : time / 24;
    return `${formattedTime.toFixed()} ${unit}`;
  };

  const isTransferPending = placementDetails.accommodationStatus?.status === TRANSFER_PENDING;
  const heroStats: RowItem[] = [
    {
      label: 'Associate Name',
      value: placementDetails.associateFullName,
      dataCy: 'placement-details__top-details__associate_full_name',
      href: `https://fclm-portal.amazon.com/search?term=${placementDetails.login}`,
    },
    {
      label: 'Start Date',
      value: placementDetails.startAtDate || '-',
      dataCy: 'placement-details__top-details__startDate',
      condition: !isTransferPending,
    },
    {
      label: 'End Date',
      value: placementDetails.endAtDate || '-',
      dataCy: 'placement-details__top-details__endDate',
      condition: !isTransferPending,
    },
    {
      label: 'Transfer Date',
      value: placementDetails.transferredAtDate || '-',
      dataCy: 'placement-details__top-details__transferDate',
      condition: isTransferPending,
    },
    {
      label: 'Time in Pending',
      value: `${formatTime(placementDetails.timeInPending || 0)}`,
      dataCy: 'placement-details__top-details__timeInPending',
      condition: [
        PLACEMENT_DECISION_PENDING,
        DLS_REFERRAL,
        TWP_BUT_PENDING_UPDATED_RESTRICTIONS,
        ASSOCIATE_DECISION_PENDING,
      ].includes(placementDetails.accommodationStatus?.status),
    },
    {
      label: 'Transfer From',
      value: placementDetails.previousFc || '-',
      dataCy: 'placement-details__top-details__transferFrom',
      condition: isTransferPending,
    },
    {
      label: 'Time in Active',
      value: `${placementDetails.placementTime || 0} days`,
      dataCy: 'placement-details__top-details__timeInActive',
      condition: [APPROVED_TWP, INDEFINITE_RESTRICTIONS].includes(placementDetails.accommodationStatus?.status),
    },
    {
      label: 'Time in Transfer',
      value: `${placementDetails.timeInTransferPending || 0} days`,
      dataCy: 'placement-details__top-details__timeInTransfer',
      condition: isTransferPending,
    },
    {
      label: 'Total Days in Placement(s)',
      value: `${placementDetails.totalAccommodatedDaysByAssociate || 0} days`,
      dataCy: 'placement-details__top-details__totalDays',
      condition:
        placementDetails.countryId !== undefined && placementDetails.countryId !== COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID,
    },
    {
      label: 'Requestor',
      value: placementDetails.requesterLogin,
      dataCy: 'placement-details__top-details__requestor',
      href: `https://phonetool.amazon.com/users/${placementDetails.requesterLogin}`,
    },
    {
      label: 'Decision Maker',
      value: placementDetails.decisionByLogin,
      dataCy: 'placement-details__top-details__approver',
      condition: !!placementDetails.decisionByLogin,
      href: `https://phonetool.amazon.com/users/${placementDetails.decisionByLogin}`,
    },
  ];

  return (
    <Row spacing="600">
      {heroStats
        .filter((stat) => stat.condition ?? true)
        .map((stat) => (
          <Column key={stat.dataCy} spacing="100">
            <Text type="b200" color="secondary">
              {stat.label}
            </Text>
            <Text type="b300" data-cy={stat.dataCy}>
              {stat.href ? (
                <Link type="secondary" href={stat.href} target="_blank" rel="noopener noreferrer">
                  {stat.value}
                </Link>
              ) : (
                stat.value
              )}
            </Text>
          </Column>
        ))}
    </Row>
  );
};

export default TopDetails;
